/**
 * Sidebar / list preview for openai_chat_history.messages arrays.
 * Use the first user turn (topic) instead of the last user message (often "ok", "yes").
 */
export function getOpenAiChatSidebarPreview(
  messages: Array<{ role?: string | null; message?: string | null }> | null | undefined,
  rawMessage?: string | null
): string {
  const list = Array.isArray(messages) && messages.length > 0 ? messages : parseConversationFromRaw(rawMessage)
  for (const item of list) {
    if (item?.role !== 'user') continue
    const text = String(item?.message ?? '').trim()
    if (text) return collapsePreviewWhitespace(text)
  }
  return ''
}

function collapsePreviewWhitespace(s: string): string {
  return s.replace(/\s+/g, ' ').trim()
}

type ChatHistoryRow = {
  messages?: Array<{ role?: string | null; message?: string | null }> | null
  message?: string | null
  firstUserMessage?: string | null
  analytics?: string | Record<string, unknown> | null
}

/**
 * Prefer `analytics.sidebarTitle` (LLM-generated on the server, no extra column).
 * Falls back to first user turn from `messages`.
 */
export function getChatHistoryListPreview(chat: ChatHistoryRow | null | undefined): string {
  if (!chat) return ''
  let analyticsObj: Record<string, unknown> | null = null
  const raw = chat.analytics
  if (raw != null) {
    if (typeof raw === 'string') {
      try {
        analyticsObj = JSON.parse(raw) as Record<string, unknown>
      } catch {
        analyticsObj = null
      }
    } else if (typeof raw === 'object') {
      analyticsObj = raw as Record<string, unknown>
    }
  }
  const fromAnalytics = typeof analyticsObj?.sidebarTitle === 'string' ? analyticsObj.sidebarTitle.trim() : ''
  if (fromAnalytics) return collapsePreviewWhitespace(fromAnalytics)
  if (typeof chat.firstUserMessage === 'string' && chat.firstUserMessage.trim()) {
    return collapsePreviewWhitespace(chat.firstUserMessage)
  }
  return getOpenAiChatSidebarPreview(chat.messages, chat.message)
}

function parseConversationFromRaw(rawMessage?: string | null): Array<{ role: 'user' | 'bot'; message: string }> {
  const text = String(rawMessage ?? '')
  if (!text) return []

  const USER_PREFIX = '|||{\\u}|||'
  const BOT_PREFIX = '|||{\\b}|||'
  if (text.includes(USER_PREFIX) || text.includes(BOT_PREFIX)) {
    const out: Array<{ role: 'user' | 'bot'; message: string }> = []
    const prefixes: Array<{ token: string; role: 'user' | 'bot' }> = [
      { token: USER_PREFIX, role: 'user' },
      { token: BOT_PREFIX, role: 'bot' },
    ]
    let cursor = 0
    const nextPrefix = (start: number) => {
      let selected: { idx: number; token: string; role: 'user' | 'bot' } | null = null
      for (const meta of prefixes) {
        const idx = text.indexOf(meta.token, start)
        if (idx < 0) continue
        if (!selected || idx < selected.idx) selected = { idx, token: meta.token, role: meta.role }
      }
      return selected
    }
    while (cursor < text.length) {
      const current = nextPrefix(cursor)
      if (!current) break
      const messageStart = current.idx + current.token.length
      const next = nextPrefix(messageStart)
      const messageEnd = next ? next.idx : text.length
      out.push({ role: current.role, message: text.slice(messageStart, messageEnd) })
      cursor = messageEnd
    }
    return out
  }

  return text.split('|||||').map((part, index) => ({
    role: index % 2 === 0 ? 'user' : 'bot',
    message: part,
  }))
}
